import { Observable } from 'rxjs';
import { inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { CategorizedTasksType, FilterQuery } from '@core/types';
import {
  GroupEntity,
  GroupEventEntity,
  GroupEventParticipant,
  GroupEventsList,
  GroupMembersList,
  GroupParticipant,
  GroupsList,
  GroupTasksList,
  NewsEntity,
  NewsList,
  Pagination,
  TaskEntity,
  UserEntity,
} from '@core/entities';

import { plainToClass, plainToInstance } from 'class-transformer';
import { GenerateImageUrlWithToken, GetUserId } from '@core/utils';
import { GroupEventsService, GroupMembersService, GroupNewsService, GroupsService, GroupTasksService } from '@core/services/groups';
import { ImageConversionService } from '@core/services/image-conversion.service';
import { UserService } from '@core/services/user.service';
import { SectionType, SubtaskStatus, TaskStatus } from '@core/enums';
import { AcceptDenyService } from '@core/services';

export class UseGroups {
  private readonly _groupsService = inject(GroupsService);
  private readonly _groupMembersService = inject(GroupMembersService);
  private readonly _groupNewsService = inject(GroupNewsService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _imageConversionService = inject(ImageConversionService);
  private readonly _groupTasksService = inject(GroupTasksService);
  private readonly _groupEventsService = inject(GroupEventsService);
  private readonly _userService = inject(UserService);
  private readonly _userId = +GetUserId();
  private readonly _acceptDenyService = inject(AcceptDenyService);

  // ----------- GROUPS -------------

  getApprovedGroups(queryParams?: FilterQuery): Observable<GroupsList> {
    return this._groupsService.find(queryParams).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            groups: response.groups.map((item: GroupEntity) => {
              const transformedItem = plainToClass(GroupEntity, item);
              if (transformedItem.id && item.image) {
                transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.GROUP);
              }
              return transformedItem;
            }),
          } as GroupsList;
        }
        return null;
      }),
    );
  }

  getGroupDetails(groupId: number): Observable<GroupEntity> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupsService.get(groupId).pipe(
      map((response) => {
        if (response && response.groups.length) {
          const group = plainToClass(GroupEntity, response.groups[0]);
          if (group.id && group.image) {
            group.imageUrl = GenerateImageUrlWithToken(group.id, SectionType.GROUP);
          }

          if (group?.updatedRecord) {
            const parsedGroup = JSON.parse(group.updatedRecord as any);
            parsedGroup.participants = JSON.parse(parsedGroup.participants as any);
            group.updatedRecord = plainToInstance(GroupEntity, parsedGroup) as GroupEntity;
            if (group.updatedRecord.image) {
              group.updatedRecord.imageUrl = GenerateImageUrlWithToken(group.updatedRecord.id, SectionType.GROUP);
            }
          }

          return group;
        }
        return null;
      }),
    );
  }

  deleteGroup(groupId: number): Observable<any> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupsService.delete(groupId);
  }

  leaveGroup(groupId: number): Observable<any> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    if (!this._userId) {
      throw new Error('userId is required');
    }
    return this._groupsService.leave(groupId, this._userId);
  }

  joinGroup(groupId: number): Observable<any> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    if (!this._userId) {
      throw new Error('userId is required');
    }
    return this._groupsService.join(groupId, this._userId);
  }

  approveGroup(groupId: number): Observable<any> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._acceptDenyService.approvedGroup(groupId, +GetUserId());
  }

  approveGroupUpdate(groupId: number): Observable<any> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._acceptDenyService.acceptUpdatedGroup(groupId, +GetUserId());
  }

  denyGroup(groupId: number, reason: string): Observable<any> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    if (!reason) {
      throw new Error('reason is required');
    }
    const data = {
      deny_reason: reason,
      deny_by_id: +GetUserId(),
    };
    return this._acceptDenyService.unapproveGroup(groupId, data);
  }

  getAllGroups(): Observable<GroupEntity[]> {
    return this._groupsService.getAll().pipe(
      map((response) => {
        if (response) {
          return response.groups;
        }
      }),
    );
  }

  createGroup(formData: FormData) {
    return this._groupsService.create(formData);
  }

  updateGroup(groupId: number, formData: FormData) {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupsService.update(groupId, formData);
  }

  getApprovedGroupUsers(groupId: number): Observable<GroupEntity> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupsService.getApprovedGroupUsers(groupId).pipe(map((response) => plainToInstance(GroupEntity, response)));
  }

  getGroupsByUser(): Observable<GroupEntity[]> {
    return this._groupsService.getGroupsByUserId(this._userId).pipe(
      map((response) => {
        if (response) {
          return response.map((item: GroupEntity) => {
            if (item.id && item.image) {
              item.imageUrl = GenerateImageUrlWithToken(item.id, SectionType.GROUP);
            }
            return plainToInstance(GroupEntity, item);
          });
        }
      }),
    );
  }

  getGroupParticipantsUsers(groupId: number): Observable<UserEntity[]> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupsService.getGroupParticipants(groupId).pipe(
      map((response) => {
        if (response) {
          return response.map((item: UserEntity) => {
            return plainToInstance(UserEntity, item);
          });
        }
        return null;
      }),
    );
  }

  // ----------- TASKS -------------

  getGroupTasks(groupId: any, filterQuery?: FilterQuery): Observable<GroupTasksList> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupTasksService.find(groupId, filterQuery).pipe(
      map((response) => {
        if (response) {
          return {
            groupTask: response.groupTask.map((item: TaskEntity) => {
              const transformedTask = plainToClass(TaskEntity, item);
              if (transformedTask && transformedTask.id) {
                transformedTask.imageUrl = GenerateImageUrlWithToken(transformedTask.id, SectionType.TASK);
              }
              return transformedTask;
            }),
          };
        }
        return null;
      }),
    );
  }

  getGroupTaskById(taskId: number): Observable<TaskEntity> {
    if (!taskId) {
      throw new Error('taskId is required');
    }
    return this._groupTasksService.get(taskId).pipe(
      map((response) => {
        if (response) {
          const task = plainToClass(TaskEntity, response);
          if (task && task.id) {
            task.imageUrl = GenerateImageUrlWithToken(task.id, SectionType.TASK);
          }
          return task;
        }
        return null;
      }),
    );
  }

  setSubTaskStatus(subTaskId: number, status?: SubtaskStatus): Observable<any> {
    if (!subTaskId) {
      throw new Error('subTaskId is required');
    }
    return this._groupTasksService.updateStatus(subTaskId);
  }

  // ----------- MEMBERS -------------

  getGroupMembers(groupId: number, queryParams?: FilterQuery): Observable<GroupMembersList> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupMembersService.get(groupId, queryParams).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            members: response.members.map((item: GroupParticipant) => {
              const transformedEntity = plainToClass(GroupParticipant, item);
              if (transformedEntity?.user?.deleted) {
                transformedEntity.user.firstName = 'Deleted';
                transformedEntity.user.lastName = 'User';
                transformedEntity.user.memberId = '0';
              }
              return transformedEntity;
            }),
          } as GroupMembersList;
        }
        return null;
      }),
    );
  }

  // ----------- NEWS -------------

  getGroupNews(groupId: number, queryParams?: FilterQuery): Observable<NewsList> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupNewsService.find(groupId, queryParams).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            news: response.news.map((item: NewsEntity) => {
              const transformedItem = plainToClass(NewsEntity, item);
              if (transformedItem.id && transformedItem.imageUrls) {
                transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.NEWS);
              }
              if (transformedItem.user?.deleted) {
                transformedItem.user.firstName = 'Deleted';
                transformedItem.user.lastName = 'User';
                transformedItem.user.memberId = '0';
              }
              return transformedItem;
            }),
          } as NewsList;
        }
        return null;
      }),
    );
  }

  deleteGroupNews(newsId: number): Observable<any> {
    if (!newsId) {
      throw new Error('newsId is required');
    }
    return this._groupNewsService.delete(newsId);
  }

  getGroupNewsById(newsId: number): Observable<NewsEntity> {
    if (!newsId) {
      throw new Error('newsId is required');
    }
    return this._groupNewsService.get(newsId).pipe(map((response) => plainToClass(NewsEntity, response)));
  }

  // ----------- EVENTS -------------

  getGroupEvents(groupId: number, pagination: Partial<Pagination>): Observable<GroupEventsList> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupEventsService.find(groupId, pagination).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            groupEvents: response.groupEvents.map((item: GroupEventEntity) => {
              const transformedItem = plainToClass(GroupEventEntity, item);
              if (transformedItem.eventImages && transformedItem.eventImages[0]?.eventImage) {
                transformedItem.eventImages[0].eventImage = this.convertBase64ToImage(transformedItem.eventImages[0]?.eventImage);
              }
              return transformedItem;
            }),
          } as GroupEventsList;
        }
        return null;
      }),
    );
  }

  getAllGroupEvents(groupId: number, queryParams?: FilterQuery): Observable<GroupEventEntity[]> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupEventsService.getAll(groupId, queryParams).pipe(
      map((response) => {
        if (response) {
          return response.map((item: GroupEventEntity) => {
            return plainToClass(GroupEventEntity, item);
          });
        }
        return null;
      }),
    );
  }

  getGroupEventById(eventId: number): Observable<GroupEventEntity> {
    if (!eventId) {
      throw new Error('eventId is required');
    }
    return this._groupEventsService.get(eventId).pipe(
      map((response) => {
        if (response) {
          const event = plainToClass(GroupEventEntity, response);
          if (event.eventImages && event.eventImages[0]?.eventImage) {
            event.eventImages[0].eventImage = this.convertBase64ToImage(event.eventImages[0]?.eventImage);
          }
          return event;
        }
        return null;
      }),
    );
  }

  getGroupEventApprovedParticipant(eventId: number): Observable<any> {
    if (!eventId) {
      throw new Error('Event Id is required');
    }
    return this._groupEventsService.getApprovedParticipants(eventId).pipe(
      map((response) => {
        if (response) {
          return response.map((item) => {
            return plainToClass(GroupEventParticipant, item);
          });
        }
        return null;
      }),
    );
  }

  getGroupEventUnApprovedParticipant(eventId: number): Observable<UserEntity[]> {
    if (!eventId) {
      throw new Error('Event Id is required');
    }
    return this._groupEventsService.getUnApprovedParticipants(eventId).pipe(
      map((response) => {
        if (response) {
          return response.map((item: UserEntity) => {
            return plainToClass(UserEntity, item);
          });
        }
        return null;
      }),
    );
  }

  getFilteredGroupEvents(groupId: number, queryParams?: FilterQuery): Observable<GroupEventEntity[]> {
    if (!groupId) {
      throw new Error('groupId is required');
    }
    return this._groupEventsService.getAll(groupId, queryParams).pipe(
      map((response) => {
        if (response) {
          return response.map((item: GroupEventEntity) => {
            const transformedItem = plainToClass(GroupEventEntity, item);
            if (transformedItem.eventImages && transformedItem.eventImages.length && transformedItem.eventImages[0]?.eventImage) {
              transformedItem.eventImages[0].eventImage = this.convertBase64ToImage(transformedItem.eventImages[0]?.eventImage);
            }
            return transformedItem;
          });
        }
        return null;
      }),
    );
  }

  deleteGroupEvent(eventId: number): Observable<any> {
    if (!eventId) {
      throw new Error('eventId is required');
    }
    return this._groupEventsService.delete(eventId);
  }

  participateGroupEvent(eventId: number): Observable<any> {
    if (!eventId) {
      throw new Error('eventId is required');
    }
    return this._groupEventsService.accept(eventId);
  }

  cancelGroupEvent(eventId: number): Observable<any> {
    if (!eventId) {
      throw new Error('eventId is required');
    }
    return this._groupEventsService.deny(eventId);
  }

  // ----------- USER PHOTO-------------

  getUserPhoto(memberId: number) {
    return this._userService.getUserProfilePhoto(memberId).pipe(
      map((resp) => {
        if (resp) {
          return this._imageConversionService.convertBase64ToBlobUrl(resp);
        }
        return null;
      }),
    );
  }

  // ----------- AUX -------------

  convertBase64ToImage(image: string) {
    if (!image) {
      return null;
    }
    const base64Data = image.substring(20);
    return this._sanitizer.bypassSecurityTrustUrl(this._imageConversionService.convertBase64ToBlobUrl(base64Data)) as string;
  }

  categorizeTasks(tasks: TaskEntity[]): CategorizedTasksType {
    return tasks?.reduce(
      (acc, task) => {
        switch (task.status) {
          case TaskStatus.ToDo:
            acc[TaskStatus.ToDo].push(task);
            break;
          case TaskStatus.InProgress:
            acc[TaskStatus.InProgress].push(task);
            break;
          case TaskStatus.Completed:
            acc[TaskStatus.Completed].push(task);
            break;
        }
        return acc;
      },
      { [TaskStatus.ToDo]: [], [TaskStatus.InProgress]: [], [TaskStatus.Completed]: [] },
    );
  }
}
